"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import type { Palette, FontOption } from "./data";
import { loc } from "./helpers";

export function CatalogContactForm({
  customColors,
  selectedPalette,
  selectedFont,
  locale,
}: {
  customColors: string[];
  selectedPalette: Palette;
  selectedFont: FontOption;
  locale: string;
}) {
  const t = useTranslations("catalog.inquiry");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
  }>({});
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");

  function validate() {
    const next: { name?: string; email?: string; phone?: string } = {};
    if (!name.trim()) next.name = t("formValidation.nameRequired");
    if (!email.trim()) next.email = t("formValidation.emailRequired");
    else if (!/^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]+$/.test(email))
      next.email = t("formValidation.emailInvalid");
    if (!phone.trim()) next.phone = t("formValidation.phoneRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      const res = await fetch("/api/catalog-inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          message,
          homepage_url: honeypot,
          selectedPalette: loc(selectedPalette.name, locale),
          selectedColors: customColors,
          selectedFont: selectedFont.name,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error("failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  const paletteName = loc(selectedPalette.name, locale);

  return (
    <section
      className="mt-16 border-t pt-16"
      style={{ borderColor: "rgba(245,245,247,0.08)" }}
    >
      <div className="mx-auto max-w-2xl">
        {/* header */}
        <p
          className="mb-2 text-xs font-semibold uppercase tracking-[0.2em]"
          style={{ color: "#3B7EFF" }}
        >
          {t("eyebrow")}
        </p>
        <h2
          className="mb-2 text-2xl font-bold tracking-tight md:text-3xl"
          style={{ color: "#F5F5F7" }}
        >
          {t("title")}
        </h2>
        <p className="mb-8 text-sm" style={{ color: "#8B8B94" }}>
          {t("subtitle")}
        </p>

        {/* selected design summary */}
        <div
          className="mb-8 rounded-xl border p-5"
          style={{
            borderColor: "rgba(245,245,247,0.08)",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <p
            className="mb-4 text-xs font-semibold uppercase tracking-[0.15em]"
            style={{ color: "#8B8B94" }}
          >
            {t("selectedDesign")}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            {/* color swatches */}
            <div>
              <p
                className="mb-1.5 text-[10px] uppercase tracking-widest"
                style={{ color: "#5A5A64" }}
              >
                {t("colorsLabel")}
              </p>
              <div className="flex gap-1.5">
                {customColors.map((color, i) => (
                  <div
                    key={i}
                    className="h-8 w-8 rounded-lg"
                    style={{
                      background: color,
                      boxShadow: "0 0 0 1px rgba(245,245,247,0.12)",
                    }}
                    title={color.toUpperCase()}
                  />
                ))}
              </div>
            </div>
            {/* palette name */}
            <div>
              <p
                className="mb-1.5 text-[10px] uppercase tracking-widest"
                style={{ color: "#5A5A64" }}
              >
                {t("paletteLabel")}
              </p>
              <p className="text-sm font-semibold" style={{ color: "#F5F5F7" }}>
                {paletteName}
              </p>
            </div>
            {/* font */}
            <div>
              <p
                className="mb-1.5 text-[10px] uppercase tracking-widest"
                style={{ color: "#5A5A64" }}
              >
                {t("fontLabel")}
              </p>
              <p
                className="text-sm font-semibold"
                style={{
                  color: "#F5F5F7",
                  fontFamily: `"${selectedFont.name}", sans-serif`,
                }}
              >
                {selectedFont.name}
              </p>
            </div>
          </div>
        </div>

        {/* success state */}
        {status === "success" ? (
          <div
            className="rounded-xl border px-6 py-8 text-center"
            style={{
              borderColor: "rgba(52,211,153,0.3)",
              background: "rgba(52,211,153,0.05)",
            }}
          >
            <p className="text-sm font-semibold" style={{ color: "#34D399" }}>
              {t("formStatus.success")}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {/* honeypot */}
            <input
              type="text"
              name="homepage_url"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              className="absolute -left-[9999px] h-0 w-0 opacity-0"
              autoComplete="off"
            />

            <div className="flex flex-col gap-4">
              {/* name */}
              <div>
                <label
                  className="mb-1.5 block text-xs font-semibold"
                  style={{ color: "#C7C7CE" }}
                >
                  {t("formLabel_name")}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name)
                      setErrors((p) => ({ ...p, name: undefined }));
                  }}
                  placeholder={t("formPlaceholder_name")}
                  className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: errors.name
                      ? "1px solid rgba(239,68,68,0.6)"
                      : "1px solid rgba(245,245,247,0.1)",
                    color: "#F5F5F7",
                  }}
                />
                {errors.name && (
                  <p className="mt-1 text-xs" style={{ color: "#EF4444" }}>
                    {errors.name}
                  </p>
                )}
              </div>

              {/* email */}
              <div>
                <label
                  className="mb-1.5 block text-xs font-semibold"
                  style={{ color: "#C7C7CE" }}
                >
                  {t("formLabel_email")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email)
                      setErrors((p) => ({ ...p, email: undefined }));
                  }}
                  placeholder={t("formPlaceholder_email")}
                  className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: errors.email
                      ? "1px solid rgba(239,68,68,0.6)"
                      : "1px solid rgba(245,245,247,0.1)",
                    color: "#F5F5F7",
                  }}
                />
                {errors.email && (
                  <p className="mt-1 text-xs" style={{ color: "#EF4444" }}>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* phone */}
              <div>
                <label
                  className="mb-1.5 block text-xs font-semibold"
                  style={{ color: "#C7C7CE" }}
                >
                  {t("formLabel_phone")}
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (errors.phone)
                      setErrors((p) => ({ ...p, phone: undefined }));
                  }}
                  placeholder={t("formPlaceholder_phone")}
                  className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: errors.phone
                      ? "1px solid rgba(239,68,68,0.6)"
                      : "1px solid rgba(245,245,247,0.1)",
                    color: "#F5F5F7",
                  }}
                />
                {errors.phone && (
                  <p className="mt-1 text-xs" style={{ color: "#EF4444" }}>
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* message */}
              <div>
                <label
                  className="mb-1.5 block text-xs font-semibold"
                  style={{ color: "#C7C7CE" }}
                >
                  {t("formLabel_message")}
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("formPlaceholder_message")}
                  rows={4}
                  className="w-full resize-none rounded-lg px-4 py-3 text-sm outline-none transition-all"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(245,245,247,0.1)",
                    color: "#F5F5F7",
                  }}
                />
              </div>

              {status === "error" && (
                <p className="text-sm" style={{ color: "#EF4444" }}>
                  {t("formStatus.error")}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="rounded-xl px-6 py-3.5 text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: "#1E3FE8", color: "#fff" }}
              >
                {status === "sending" ? t("formSubmitting") : t("formSubmit")}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
